import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  XCircle, LayoutDashboard, RefreshCw, Loader2, FileText,
  AlertTriangle, Info, Mail, ShieldAlert, Clock, Wallet, CreditCard,
} from 'lucide-react';
import axios from 'axios';

// iter-255 — ASB606 public receipt endpoint (source of truth for the
// invoice's `checkout_url` so the retry button opens the real gateway
// URL instead of a made-up local /pay/:id route.
const RECEIPT_API = 'https://asb606.com/api/public/receipt';

// CloudGM Business brand palette — same as PaymentSuccess.
const BRAND = {
  primary: '#38BDF8',
  primaryDark: '#0284C7',
  accent: '#F97316',
  accentDark: '#C2410C',
};

// Standard failure reason catalogue. Keyed by `reason` query param, with
// a human bilingual title + guidance. Falls back to a generic entry.
const REASONS = {
  user_cancelled: {
    icon: XCircle,
    titleAr: 'ألغيتَ عملية الدفع',
    titleEn: 'You cancelled the payment',
    descAr: 'لقد أغلقت نافذة الدفع قبل اكتمال العملية. لم يتم خصم أي مبلغ.',
    descEn: 'The payment window was closed before completion. No amount was charged.',
    tone: 'warning',
  },
  card_declined: {
    icon: CreditCard,
    titleAr: 'رفض البنك البطاقة',
    titleEn: 'Card declined by the issuer',
    descAr: 'لم يقبل البنك المُصدر هذه البطاقة. جرّب بطاقة أخرى أو طريقة دفع مختلفة.',
    descEn: 'The issuing bank refused this card. Try another card or a different method.',
    tone: 'error',
  },
  insufficient_funds: {
    icon: Wallet,
    titleAr: 'الرصيد غير كافٍ',
    titleEn: 'Insufficient funds',
    descAr: 'الرصيد المتاح لا يغطي قيمة الفاتورة. أعد المحاولة بعد التأكد من الرصيد أو استخدم طريقة أخرى.',
    descEn: 'Available balance does not cover the invoice. Top up or try another method.',
    tone: 'error',
  },
  expired: {
    icon: Clock,
    titleAr: 'انتهت صلاحية الفاتورة',
    titleEn: 'Invoice expired',
    descAr: 'انقضت المدة المحدَّدة للدفع. اطلب فاتورة جديدة من الوكيل.',
    descEn: 'The payment window for this invoice has expired. Request a new invoice from your agent.',
    tone: 'warning',
  },
  three_ds_failed: {
    icon: ShieldAlert,
    titleAr: 'فشل التحقق الآمن 3-D Secure',
    titleEn: '3-D Secure verification failed',
    descAr: 'لم يكتمل التحقق الآمن من بنكك. حاول مرة أخرى، أو استخدم بطاقة أخرى.',
    descEn: "Your bank's 3-D Secure challenge failed. Try again or use another card.",
    tone: 'error',
  },
  gateway_error: {
    icon: AlertTriangle,
    titleAr: 'خطأ من بوابة الدفع',
    titleEn: 'Payment gateway error',
    descAr: 'حدث خطأ مؤقت لدى مزوّد الدفع. أعد المحاولة بعد لحظات.',
    descEn: 'A temporary error occurred at the payment provider. Please retry in a moment.',
    tone: 'error',
  },
  timeout: {
    icon: Clock,
    titleAr: 'انتهت المهلة',
    titleEn: 'Timed out',
    descAr: 'لم تكتمل العملية خلال الوقت المسموح. أعد المحاولة.',
    descEn: 'The transaction did not complete in time. Please try again.',
    tone: 'warning',
  },
};

const GENERIC = {
  icon: XCircle,
  titleAr: 'تم إلغاء عملية الدفع',
  titleEn: 'Payment cancelled',
  descAr: 'لم يتم إتمام عملية الدفع. لم يتم خصم أي مبلغ من حسابك.',
  descEn: 'Your payment was not completed. No amount has been charged to your account.',
  tone: 'warning',
};

export default function PaymentCancelled() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const invoiceId = searchParams.get('inv') || searchParams.get('invoice') || '';
  const reasonKey = (searchParams.get('reason') || '').toLowerCase().trim();
  const errorCode = (searchParams.get('error') || searchParams.get('code') || '').trim();
  const errorMessage = (searchParams.get('message') || searchParams.get('error_message') || '').trim();
  const providerParam = (searchParams.get('provider') || '').trim();

  const langParam = searchParams.get('lang');
  const [lang, setLang] = useState(
    langParam === 'en' ? 'en' : langParam === 'ar' ? 'ar' : (document.documentElement.dir === 'rtl' ? 'ar' : 'ar')
  );
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [provider, setProvider] = useState(providerParam || null);
  const [retryLoading, setRetryLoading] = useState(false);

  const isRTL = lang === 'ar';
  const reason = useMemo(() => REASONS[reasonKey] || GENERIC, [reasonKey]);
  const ReasonIcon = reason.icon;
  const isError = reason.tone === 'error';

  // iter-255 — pre-fetch the receipt so we know the real checkout_url
  // as soon as the page mounts. Retry becomes a one-click op.
  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    axios.get(`${RECEIPT_API}/${encodeURIComponent(invoiceId)}`, { timeout: 12000 })
      .then((r) => {
        if (cancelled) return;
        setCheckoutUrl(r.data?.checkout_url || null);
        if (!provider) setProvider(r.data?.payment_method || null);
      })
      .catch(() => { /* silent — retry button still falls back to home */ });
    return () => { cancelled = true; };
  }, [invoiceId, provider]);

  const goDashboard = () => navigate('/dashboard');
  const goPayments = () => navigate('/payments');
  const retry = async () => {
    // iter-255 — Open the ACTUAL gateway checkout_url from the ASB606
    // receipt. If we already have it (pre-fetched), open immediately.
    // Otherwise fetch once more; last resort go to the payments page.
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
      return;
    }
    if (!invoiceId) { navigate('/payments'); return; }
    setRetryLoading(true);
    try {
      const r = await axios.get(`${RECEIPT_API}/${encodeURIComponent(invoiceId)}`, { timeout: 12000 });
      const url = r.data?.checkout_url;
      if (url) window.location.href = url;
      else navigate('/payments');
    } catch {
      navigate('/payments');
    } finally {
      setRetryLoading(false);
    }
  };

  const headerColors = isError
    ? { from: '#EF4444', to: '#B91C1C' }   // red
    : { from: BRAND.accent, to: BRAND.accentDark }; // orange / warning

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="min-h-screen py-8 px-4 sm:px-6"
      style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF2F2 50%, #F0F9FF 100%)' }}
      data-testid="payment-cancelled-page"
    >
      {/* Shake keyframes — custom brand animation for failure */}
      <style>{`
        @keyframes cgm-shake {
          0%,100% { transform: translate3d(0,0,0); }
          15% { transform: translate3d(-6px,0,0); }
          30% { transform: translate3d(5px,0,0); }
          45% { transform: translate3d(-4px,0,0); }
          60% { transform: translate3d(3px,0,0); }
          75% { transform: translate3d(-2px,0,0); }
        }
        .cgm-shake { animation: cgm-shake 0.9s ease-out both; }
      `}</style>

      {/* Language switch */}
      <div className="max-w-2xl mx-auto flex justify-end mb-4">
        <button
          onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-gray-200 text-gray-700 hover:bg-white transition-colors"
          data-testid="lang-toggle"
        >
          {lang === 'ar' ? 'English' : 'العربية'}
        </button>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-500" data-testid="cancelled-card">
          {/* Header */}
          <div className="p-8 sm:p-10 text-white text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${headerColors.from} 0%, ${headerColors.to} 100%)` }}>
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <div className="w-24 h-24 mx-auto rounded-full bg-white/20 backdrop-blur border-2 border-white/40 flex items-center justify-center cgm-shake" data-testid="cancel-icon-wrap">
                <ReasonIcon className="h-12 w-12" data-testid="cancel-icon" />
              </div>
              <h1 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight" data-testid="cancel-headline">
                {lang === 'ar' ? reason.titleAr : reason.titleEn}
              </h1>
              <p className="mt-2 text-white/90 text-sm max-w-md mx-auto leading-relaxed" data-testid="cancel-description">
                {lang === 'ar' ? reason.descAr : reason.descEn}
              </p>
              {invoiceId && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur border border-white/30 text-sm font-mono font-bold" data-testid="cancel-invoice-no">
                  <span className="opacity-80">{lang === 'ar' ? 'الفاتورة' : 'Invoice'}</span>
                  <span>#{invoiceId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8">
            {/* Technical error strip (only when we actually have a code/message) */}
            {(errorCode || errorMessage) && (
              <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-4" data-testid="cancel-error-detail">
                <h3 className="font-semibold text-red-900 text-sm mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" />
                  {lang === 'ar' ? 'تفاصيل الفشل' : 'Failure details'}
                </h3>
                {errorCode && (
                  <p className="text-xs text-red-800 leading-relaxed">
                    <span className="font-semibold">{lang === 'ar' ? 'الكود:' : 'Code:'} </span>
                    <code className="font-mono bg-white/60 px-1.5 py-0.5 rounded">{errorCode}</code>
                  </p>
                )}
                {errorMessage && (
                  <p className="text-xs text-red-800 leading-relaxed mt-1 break-words">
                    <span className="font-semibold">{lang === 'ar' ? 'الرسالة:' : 'Message:'} </span>
                    {errorMessage}
                  </p>
                )}
                {provider && (
                  <p className="text-xs text-red-800 leading-relaxed mt-1">
                    <span className="font-semibold">{lang === 'ar' ? 'المزوّد:' : 'Provider:'} </span>
                    {provider}
                  </p>
                )}
              </div>
            )}

            {/* Guidance box */}
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-6" data-testid="cancel-guidance">
              <h3 className="font-semibold text-amber-900 text-sm mb-2 flex items-center gap-1.5">
                <Info className="h-4 w-4" />
                {lang === 'ar' ? 'ماذا يعني هذا؟' : 'What does this mean?'}
              </h3>
              <ul className="text-xs text-amber-800 space-y-1 leading-relaxed">
                <li>• {lang === 'ar' ? 'الفاتورة لا تزال قائمة ولم تنتهِ صلاحيتها.' : 'The invoice is still open and has not expired.'}</li>
                <li>• {lang === 'ar' ? 'يمكنك المحاولة مرة أخرى بطريقة دفع مختلفة.' : 'You can retry with a different payment method.'}</li>
                <li>• {lang === 'ar' ? 'لم يُخصم أي مبلغ من بطاقتك أو حسابك.' : 'No amount has been deducted from your card or account.'}</li>
              </ul>
            </div>

            {/* Primary: Retry */}
            <button
              onClick={retry}
              disabled={retryLoading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-95 active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait"
              style={{
                background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)`,
                boxShadow: `0 10px 24px ${BRAND.primary}40`,
              }}
              data-testid="btn-retry"
            >
              {retryLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {lang === 'ar' ? 'إعادة محاولة الدفع' : 'Retry payment'}
            </button>

            {/* Navigation row */}
            <div className="flex flex-col sm:flex-row gap-3 mt-3">
              <button
                onClick={goDashboard}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 transition-colors active:scale-[0.98]"
                data-testid="btn-dashboard"
              >
                <LayoutDashboard className="h-4 w-4" />
                {lang === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to dashboard'}
              </button>
              <button
                onClick={goPayments}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 transition-colors active:scale-[0.98]"
                data-testid="btn-payments"
              >
                <FileText className="h-4 w-4" />
                {lang === 'ar' ? 'عرض سجل المدفوعات' : 'View payment history'}
              </button>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6 flex items-center justify-center gap-1">
              <Mail className="h-3 w-3" />
              {lang === 'ar'
                ? 'إذا واجهت مشكلة، تواصل مع فريق الدعم على '
                : 'If you encountered an issue, contact support at '}
              <a href="mailto:support@cloudgm.business" className="text-sky-600 hover:underline font-medium" dir="ltr">
                support@cloudgm.business
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
